/**
 * Issuer Board Resolution Signing Component
 * Handles document viewing and signing with optional liveness verification
 */

import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Center, Loader, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { toast, ToastContainer } from "react-toastify";
import DocViewer from "@cyntler/react-doc-viewer";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { Button } from "@funded-here-interface/common";
import { setRegtankLiveness } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { LivenessWebVerifyStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { isValidRegtankUrl } from "@funded-here-interface/common/src/Utils/urlValidation";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUser";
import { useDocumentVisibility } from "@funded-here-interface/common/src/hooks/useDocumentVisibility";

import { RootState } from "../../store";
import { WEB_DOMAIN } from "../../constants/domain";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import { ThanksPageType } from "../../constants/enums";
import { useIssuerBoardResolutionSign } from "../../hooks/useDocuSign";
import useGetLivenessWebVerifyLink from "../../hooks/useGetLivenessWebVerifyLink";
import useGetRegtankLivenessWebVerifyStatus from "../../hooks/useGetRegtankLivenessWebVerifyStatus";
import { useRegtankLivenessSession } from "../../hooks/useRegtankLivenessSession";
import { useRegtankLivenessValidation } from "../../hooks/useRegtankLivenessValidation";
import { navigateToThanksPage } from "../ThanksPage/ThanksPageUtils";

import { useStyles } from "./IssuerBoardResolution.styles";
import {
  useDocumentState,
  useButtonState,
  FailedLivenessModal,
  COLORS,
  MESSAGES,
} from "../../components/DocumentSigning";
import { AGREEMENT_NAME } from "../../components/DocumentSigning/constants";
import { useHandleSign } from "../../components/DocumentSigning/hooks/useHandleDocumentSign";
// Custom acknowledgement message for Issuer Board Resolution
const ISSUER_ACKNOWLEDGEMENT =
  'By clicking "Proceed to Sign", you acknowledge that you have read and understood the above Board Resolution and are signing in your capacity as a director.' as const;

const IssuerBoardResolution: FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const {
    token,
    regtankRequest,
    regtankLiveness,
    regtankLivenessFeatureFlag,
    regtankOnboardingFeatureFlag,
    regtankOnboarding,
  } = useSelector((state: RootState) => state.auth);

  // API hooks
  const issuerBoardResolutionMutation = useIssuerBoardResolutionSign();
  const livenessLinkMutation = useGetLivenessWebVerifyLink();
  const livenessStatusMutation = useGetRegtankLivenessWebVerifyStatus();
  const { data: userData, refetch } = useGetUser(token);

  // Local state
  const [regtankLivenessUrl, setRegtankLivenessUrl] = useState("");
  const [openedFailedLiveness, modalAction] = useDisclosure();

  // Custom hooks
  const documentState = useDocumentState();
  const livenessSession = useRegtankLivenessSession(
    documentState.documentId,
    userData?.user?.regtankLiveness || regtankLiveness
  );
  const livenessValidation = useRegtankLivenessValidation(livenessSession);
  const buttonState = useButtonState({
    regtankLivenessFeatureFlag: regtankLivenessFeatureFlag ?? null,
    regtankOnboardingFeatureFlag: regtankOnboardingFeatureFlag ?? null,
    regtankOnboarding: regtankOnboarding ?? null,
    regtankRequest,
    isDisabledByLiveness: livenessValidation.isDisabled,
  });

  // Document viewer config
  const docs = [{ uri: documentState.realUrl, fileType: "pdf" as const }];
  const redirectUrl = window.location.href;

  /**
   * Generate Issuer Board Resolution document
   */
  const handleGenerateDocument = async () => {
    toast.info(MESSAGES.LOADING_DOC, { toastId: "loading-issuer-board-reso" });
    try {
      issuerBoardResolutionMutation.mutate(
        {
          token,
          webDomain: WEB_DOMAIN,
          redirectUrl: `${getBaseUrl(Microservice.DOCUMENT_MANAGEMENT)}/document-management/docusign/webhook`,
          directCreateDocusign: false,
        },
        {
          onSuccess: async (data) => {
            await refetch();
            documentState.updateDocument(
              data.url,
              data.documentId,
              data.completedAt || ""
            );
            documentState.incrementViewerKey();
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    } catch (ex) {
      toast.error("Error generating Issuer Board Resolution");
    }
  };

  // Initial load: generate document
  useEffect(() => {
    handleGenerateDocument();
    documentState.incrementViewerKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDocumentVisibility(
    () => documentState.incrementViewerKey(),
    !!documentState.documentId
  );

  // Verify liveness status when needed
  useEffect(() => {
    if (!livenessSession || !livenessValidation.shouldVerify) return;

    livenessStatusMutation.mutate(
      { token, sessionId: livenessSession.sessionId },
      {
        onSuccess: (data: any) => {
          if (data === LivenessWebVerifyStatusEnum.SUCCESS) {
            handleSign();
          } else if (
            data === LivenessWebVerifyStatusEnum.LIVENESS_FAILED ||
            data === LivenessWebVerifyStatusEnum.FAILED
          ) {
            modalAction.open();
          }
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livenessSession, livenessValidation.shouldVerify, token]);

  // Set liveness URL when user can attempt verification
  useEffect(() => {
    if (livenessValidation.canAttempt) {
      setRegtankLivenessUrl(livenessSession?.regtankLivenessUrl || "");
    }
  }, [livenessValidation.canAttempt, livenessSession]);

  /**
   * Navigate to next page based on user status
   */
  const navigateNext = async () => {
    const { data: freshData } = await refetch();
    if (
      freshData.user.allShareHoldersDirectorsOnBoarded &&
      freshData.user.isAuthRep
    ) {
      navigate("/issuer-agreement");
    } else {
      navigateToThanksPage(navigate, ThanksPageType.BOARDING_RESOLUTION);
    }
  };

  // Sign the document and complete onboarding
  // Initialize the handle sign hook
  const handleSign = useHandleSign({
    documentState,
    regtankLivenessFeatureFlag,
    token,
    needToCompleteOnboarding: true,
    needToSubmitAgreements: false,
  });

  /**
   * Handle liveness verification or direct signing
   */
  const handleLivenessVerify = async () => {
    buttonState.disableButton("");

    if (!regtankLivenessFeatureFlag) {
      handleSign();
      return;
    }
    await refetch();

    // Use existing liveness URL if available
    if (regtankLivenessUrl) {
      if (!isValidRegtankUrl(regtankLivenessUrl)) {
        toast.error(MESSAGES.INVALID_URL);
        return;
      }
      window.open(regtankLivenessUrl, "_self");
      return;
    }

    // Generate new liveness URL
    toast.info(MESSAGES.LOADING_LIVENESS, {
      toastId: "loading-regtank-liveness-check-url",
    });

    livenessLinkMutation.mutate(
      {
        token,
        documentId: documentState.documentId,
        redirectUrl,
        agreementName: AGREEMENT_NAME.ISSUER_BOARD_RESO,
      },
      {
        onSuccess: (data) => {
          if (!isValidRegtankUrl(data.verifyLink)) {
            toast.error(MESSAGES.INVALID_URL);
            return;
          }

          sessionStorage.setItem("regtankSessionId", data.sessionId);

          dispatch(
            setRegtankLiveness({
              regtankLiveness: [
                {
                  id: documentState.documentId.toString(),
                  clientSessionId: documentState.documentId.toString(),
                  sessionId: data.sessionId,
                  livenessStatus: null,
                  regtankLivenessUrl: data.verifyLink,
                  noOfAttemptLiveness: 0,
                  reattemptApproval: null,
                  expiredDate: data.expiredDate,
                },
              ],
            })
          );

          window.open(data.verifyLink, "_self");
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
          documentState.incrementViewerKey();
        },
      }
    );
  };

  /**
   * Handle modal continue action
   */
  const handleContinue = () => {
    buttonState.disableButton(MESSAGES.VERIFICATION_DISABLED);
    modalAction.close();
    sessionStorage.removeItem("regtankSessionId");
    documentState.incrementViewerKey();
  };

  return (
    <>
      <ToastContainer />
      <div>
        <Text className={classes.heading}>
          {AGREEMENT_NAME.ISSUER_BOARD_RESO}
        </Text>

        {documentState.isDocumentLoaded && (
          <>
            <DocViewer
              documents={docs}
              style={{ width: "100%", height: 500, overflow: "auto" }}
              key={documentState.viewerKey}
              className={classes.docviewer}
              config={{
                header: {
                  disableHeader: false,
                  disableFileName: true,
                  retainURLParams: false,
                },
                pdfVerticalScrollByDefault: true,
              }}
            />

            {!documentState.isDocumentSigned && (
              <div className={classes.buttonContainer}>
                <Text style={{ marginBottom: 10, fontSize: 14 }}>
                  {ISSUER_ACKNOWLEDGEMENT}
                </Text>
                <Button
                  disabled={buttonState.buttonDisabled}
                  onClick={handleLivenessVerify}
                  textColor="white"
                  backgroundColor={COLORS.PRIMARY}
                  title={buttonState.titleButton}
                >
                  Proceed to Sign
                </Button>
              </div>
            )}

            {documentState.isDocumentSigned && (
              <div className={classes.buttonContainer}>
                <Button
                  onClick={navigateNext}
                  textColor="white"
                  backgroundColor={COLORS.PRIMARY}
                >
                  Continue
                </Button>
              </div>
            )}

            <FailedLivenessModal
              opened={openedFailedLiveness}
              onClose={modalAction.close}
              onContinue={handleContinue}
              classes={classes}
            />
          </>
        )}

        {!documentState.isDocumentLoaded && (
          <Center maw={400} h={500} mx="auto">
            <Loader color="teal" />
          </Center>
        )}
      </div>
    </>
  );
};

export default IssuerBoardResolution;
